// AB#1610 — synchronous evaluator for computed-field expressions, used for LIVE recalculation in the
// editable line grid (line totals, order totals) as the user types. The platform's authoritative
// computed-field evaluation is server-side JSONata (ComputedFieldEvaluator); this is a deliberately
// small client-side twin covering the arithmetic + aggregate subset those formulas use, kept
// SYNCHRONOUS so a keystroke recompute can't race (JSONata 2.x evaluate() is async).
//
// Supported grammar:
//   expr   := term (('+' | '-') term)*
//   term   := factor (('*' | '/') factor)*
//   factor := number | '-' factor | '(' expr ')' | func '(' expr ')' | path
//   func   := $sum | $avg | $count | $min | $max          (aggregate over an array-valued path)
//   path   := ident ('.' ident)*                          (dotted lookup; an array segment maps the rest)
// Anything outside this subset returns undefined (the field is left untouched), matching the
// server's best-effort "skip on failure" behaviour rather than writing a wrong value.

type Json = Record<string, unknown>

const AGGREGATES = new Set(['$sum', '$avg', '$count', '$min', '$max'])

// Resolve a dotted path against the record. When a segment holds an array, the remaining path is
// mapped across its elements (so `lines.lineTotal` → the lineTotal of every line).
function resolvePath(record: unknown, segs: string[]): unknown {
    let cur: unknown = record
    for (let i = 0; i < segs.length; i++) {
        if (Array.isArray(cur)) {
            const rest = segs.slice(i)
            return cur.map(el => resolvePath(el, rest))
        }
        if (cur && typeof cur === 'object') {
            cur = (cur as Json)[segs[i]!]
        } else {
            return undefined
        }
    }
    return cur
}

function toNumbers(value: unknown): number[] {
    const flat = Array.isArray(value) ? value.flat(Infinity) : [value]
    return flat.map(v => Number(v)).filter(n => !Number.isNaN(n))
}

function aggregate(fn: string, value: unknown): number {
    const nums = toNumbers(value)
    switch (fn) {
        case '$sum': return nums.reduce((a, b) => a + b, 0)
        case '$avg': return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0
        case '$count': return Array.isArray(value) ? value.length : (value === undefined ? 0 : 1)
        case '$min': return nums.length ? Math.min(...nums) : 0
        case '$max': return nums.length ? Math.max(...nums) : 0
        default: return 0
    }
}

// ── Tokeniser ────────────────────────────────────────────────────────────────
type Token = { kind: 'num'; value: number } | { kind: 'op'; value: string } | { kind: 'id'; value: string }

function tokenize(expr: string): Token[] | null {
    const tokens: Token[] = []
    let i = 0
    while (i < expr.length) {
        const ch = expr[i]!
        if (ch === ' ' || ch === '\t') { i++; continue }
        if ('+-*/()'.includes(ch)) { tokens.push({ kind: 'op', value: ch }); i++; continue }
        if (ch >= '0' && ch <= '9' || (ch === '.' && /[0-9]/.test(expr[i + 1] ?? ''))) {
            let j = i + 1
            while (j < expr.length && /[0-9.]/.test(expr[j]!)) { j++ }
            tokens.push({ kind: 'num', value: Number(expr.slice(i, j)) }); i = j; continue
        }
        // identifier / function: starts with $ or a letter/underscore, then word chars or dots
        if (ch === '$' || /[A-Za-z_]/.test(ch)) {
            let j = i + 1
            while (j < expr.length && /[A-Za-z0-9_.]/.test(expr[j]!)) { j++ }
            tokens.push({ kind: 'id', value: expr.slice(i, j) }); i = j; continue
        }
        return null // unsupported character
    }
    return tokens
}

// ── Recursive-descent parser/evaluator ───────────────────────────────────────
class Evaluator {
    private pos = 0
    constructor(private readonly tokens: Token[], private readonly record: Json) {}

    eval(): number {
        const v = this.expr()
        if (this.pos !== this.tokens.length) { throw new Error('trailing tokens') }
        return v
    }

    private peek(): Token | undefined { return this.tokens[this.pos] }
    private next(): Token | undefined { return this.tokens[this.pos++] }

    private expr(): number {
        let v = this.term()
        for (let t = this.peek(); t?.kind === 'op' && (t.value === '+' || t.value === '-'); t = this.peek()) {
            this.next()
            const rhs = this.term()
            v = t.value === '+' ? v + rhs : v - rhs
        }
        return v
    }

    private term(): number {
        let v = this.factor()
        for (let t = this.peek(); t?.kind === 'op' && (t.value === '*' || t.value === '/'); t = this.peek()) {
            this.next()
            const rhs = this.factor()
            v = t.value === '*' ? v * rhs : (rhs === 0 ? 0 : v / rhs)
        }
        return v
    }

    private factor(): number {
        const t = this.next()
        if (!t) { throw new Error('unexpected end') }
        if (t.kind === 'num') { return t.value }
        if (t.kind === 'op' && t.value === '-') { return -this.factor() }
        if (t.kind === 'op' && t.value === '(') {
            const v = this.expr()
            const close = this.next()
            if (!close || close.kind !== 'op' || close.value !== ')') { throw new Error('expected )') }
            return v
        }
        if (t.kind === 'id') {
            // aggregate function call: $sum( path )
            if (AGGREGATES.has(t.value)) {
                const open = this.next()
                if (!open || open.kind !== 'op' || open.value !== '(') { throw new Error('expected (') }
                const argTok = this.next()
                if (!argTok || argTok.kind !== 'id') { throw new Error('expected path arg') }
                const close = this.next()
                if (!close || close.kind !== 'op' || close.value !== ')') { throw new Error('expected )') }
                return aggregate(t.value, resolvePath(this.record, argTok.value.split('.')))
            }
            // plain field path
            const n = Number(resolvePath(this.record, t.value.split('.')))
            return Number.isNaN(n) ? 0 : n
        }
        throw new Error('unexpected token')
    }
}

/**
 * Evaluates a computed-field expression against a record, returning a number, or `undefined`
 * when the expression is empty or outside the supported subset (caller leaves the field as-is).
 */
export function evalFormula(expr: string | undefined, record: Record<string, unknown>): number | undefined {
    if (!expr || expr.trim() === '') { return undefined }
    const tokens = tokenize(expr)
    if (!tokens || tokens.length === 0) { return undefined }
    try {
        return new Evaluator(tokens, record).eval()
    } catch {
        return undefined
    }
}

/**
 * Returns a copy of `record` with every `compute`-bearing field in `meta` re-evaluated. Fields whose
 * expression is unsupported/failing are left untouched. Used to recompute line + header totals on edit.
 */
export function applyComputes(
    record: Record<string, unknown>,
    meta: Array<{ name: string; compute?: string | undefined }>,
): Record<string, unknown> {
    let changed = false
    const next = { ...record }
    for (const f of meta) {
        if (!f.compute) { continue }
        const value = evalFormula(f.compute, next)
        if (value !== undefined && value !== next[f.name]) { next[f.name] = value; changed = true }
    }
    return changed ? next : record
}
